/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { TokenMetadata } from '../lib/pinata';
import { X, Globe, Send, ExternalLink, Copy, ArrowLeftRight, FileText, User, Star, ShieldCheck, Twitter, Zap } from 'lucide-react';
import { formatCurrency, formatAddress } from '../lib/utils/format';
import { TokenInsights } from './TokenInsights';
import { useWatchlist } from '../context/WatchlistContext';
import { useAiContext } from '../providers/ai-provider';
import { TokenHeaderPlaceholder } from './token-header-placeholder';

interface TokenDetailsModalProps {
    token: TokenMetadata;
    isOpen: boolean;
    onClose: () => void;
    onTrade: () => void;
    price?: number;
    change24h?: number;
}

export const TokenDetailsModal: React.FC<TokenDetailsModalProps> = ({ token, isOpen, onClose, onTrade, price, change24h }) => {
    const { isFavorited, addToWatchlist, removeFromWatchlist } = useWatchlist();
    const { smartMoney } = useAiContext();
    const mintAddress = token.properties?.mintAddress;
    const isFavorite = mintAddress ? isFavorited(mintAddress) : false;
    
    // Check for smart money activity
    const relevantWhales = smartMoney.filter(trader => trader.lastTrade.tokenMint === mintAddress);

    if (!isOpen) return null;

    const handleFavoriteClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!mintAddress) return;
        if (isFavorite) {
            removeFromWatchlist(mintAddress);
        } else {
            addToWatchlist(mintAddress);
        }
    };

    const isSui = token.properties?.chain === 'SUI';
    const creator = token.properties?.creator;
    const deploymentDate = token.properties?.createdAt 
        ? new Date(token.properties.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) 
        : 'Unknown';
    
    return (
        <div className="fixed inset-0 z-[160] bg-black/60 backdrop-blur-md flex items-center justify-center p-2 sm:p-4 animate-fade-in" onClick={onClose}>
            <div 
                className="bg-white w-full max-w-4xl rounded-3xl shadow-2xl border border-stone-200 flex flex-col md:flex-row relative max-h-[90vh] md:h-[700px] overflow-y-auto md:overflow-hidden" 
                onClick={e => e.stopPropagation()}
            >
                <button 
                    onClick={onClose} 
                    className="absolute top-3 right-3 z-50 p-2 bg-white/90 hover:bg-white text-stone-500 rounded-full transition-colors shadow-sm border border-stone-200" 
                    title="Close"
                >
                    <X size={18} />
                </button>

                {/* LEFT COLUMN */}
                <div className="w-full md:w-5/12 bg-stone-50 flex flex-col border-b md:border-b-0 md:border-r border-stone-200 shrink-0 md:h-full md:overflow-hidden relative">
                    
                    <div className="h-36 w-full bg-stone-200 relative shrink-0 group">
                        {token.header_image ? (
                            <img src={token.header_image} className="w-full h-full object-cover" alt="Header" />
                        ) : (
                            <TokenHeaderPlaceholder />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
                    </div>

                    <div className="px-6 flex-1 flex flex-col pb-6 md:overflow-y-auto custom-scrollbar relative">
                        <div className="relative -mt-10 mb-3 flex justify-between items-end">
                            <div className="w-24 h-24 rounded-2xl border-4 border-white bg-white shadow-md overflow-hidden relative shrink-0">
                                {token.image ? (
                                    <img src={token.image} className="w-full h-full object-cover" alt={token.symbol} />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-stone-400 font-bold text-2xl">{token.symbol?.[0]}</div>
                                )}
                            </div>
                            
                            <div className="bg-white rounded-xl p-1.5 shadow-sm border border-stone-100 mb-1">
                                 <img 
                                    src={isSui ? 'https://cryptologos.cc/logos/sui-sui-logo.png' : 'https://cryptologos.cc/logos/solana-sol-logo.png'} 
                                    className="w-5 h-5 object-contain" 
                                    alt={isSui ? "Sui" : "Solana"}
                                />
                            </div>
                        </div>

                        <div className="mb-5">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <h2 className="font-serif font-bold text-2xl text-stone-900 leading-none">{token.name}</h2>
                                <button 
                                    onClick={handleFavoriteClick}
                                    className="text-stone-300 hover:text-amber-500 transition-colors pt-1"
                                >
                                    <Star size={18} className={isFavorite ? 'fill-amber-500 text-amber-500' : 'fill-transparent stroke-current'} />
                                </button>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-mono text-xs font-bold text-stone-500 bg-stone-200/50 px-1.5 py-0.5 rounded-md border border-stone-200/50">${token.symbol}</span>
                                {creator && (
                                    <span className="text-[10px] text-stone-400 flex items-center gap-1 bg-white border border-stone-200 px-2 py-0.5 rounded-md" title="Creator">
                                        <User size={10} /> {formatAddress(creator)}
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="flex items-end gap-4 mb-6 bg-white p-4 rounded-xl border border-stone-200 shadow-sm">
                            <div>
                                <div className="text-[10px] text-stone-400 font-bold uppercase mb-0.5">Price</div>
                                <div className="font-mono text-2xl font-bold text-stone-900 leading-none">
                                    {price !== undefined ? formatCurrency(price, 'USD', price < 0.01 ? 6 : 2) : '-'}
                                </div>
                            </div>
                            {change24h !== undefined && (
                                <div className="mb-0.5">
                                    <div className="text-[10px] text-stone-400 font-bold uppercase mb-0.5">24h Change</div>
                                    <div className={`text-sm font-bold ${change24h >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        {change24h > 0 ? '+' : ''}{change24h.toFixed(2)}%
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="mb-6 flex-1 min-h-0">
                            <h3 className="text-[10px] font-bold text-stone-400 uppercase mb-2 flex items-center gap-1"><FileText size={12} /> About</h3>
                            <div className="text-sm text-stone-600 leading-relaxed md:max-h-[150px] md:overflow-y-auto custom-scrollbar pr-2">
                                {token.description || "No description provided for this project."}
                            </div>
                        </div>

                        <div className="grid grid-cols-3 gap-2 mt-auto pb-4 md:pb-0">
                            {token.properties?.website && (
                                <a href={token.properties.website} target="_blank" rel="noreferrer" className="py-2.5 bg-white border border-stone-200 rounded-xl flex items-center justify-center text-stone-600 hover:text-nobel-gold hover:border-nobel-gold transition-colors hover:shadow-sm">
                                    <Globe size={16} />
                                </a>
                            )}
                            {token.properties?.twitter && (
                                <a href={token.properties.twitter} target="_blank" rel="noreferrer" className="py-2.5 bg-white border border-stone-200 rounded-xl flex items-center justify-center text-stone-600 hover:text-nobel-gold hover:border-nobel-gold transition-colors hover:shadow-sm">
                                    <Twitter size={16} />
                                </a>
                            )}
                            {token.properties?.telegram && (
                                <a href={token.properties.telegram} target="_blank" rel="noreferrer" className="py-2.5 bg-white border border-stone-200 rounded-xl flex items-center justify-center text-stone-600 hover:text-nobel-gold hover:border-nobel-gold transition-colors hover:shadow-sm">
                                    <Send size={16} />
                                </a>
                            )}
                        </div>
                    </div>
                </div>

                {/* RIGHT COLUMN */}
                <div className="w-full md:w-7/12 bg-white flex flex-col relative md:h-full md:overflow-hidden">
                    
                    <div className="flex-1 md:overflow-y-auto custom-scrollbar p-6 pb-36 md:pb-24">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="font-bold text-stone-900 flex items-center gap-2">
                                <ShieldCheck size={18} className="text-nobel-gold"/> Project Analytics
                            </h3>
                            <div className="text-[10px] font-mono text-stone-400 bg-stone-50 px-2 py-1 rounded-md border border-stone-100">
                                Launched: {deploymentDate}
                            </div>
                        </div>

                        <div className="mb-6">
                             <TokenInsights token={token} currentPrice={price || 0} />
                        </div>

                        {/* Smart Money Section */}
                        {relevantWhales.length > 0 && (
                            <div className="bg-stone-50 rounded-xl p-4 border border-stone-200">
                                <h4 className="text-xs font-bold text-stone-500 uppercase mb-3 flex items-center gap-2">
                                    <Zap size={14} className="text-purple-600"/> Smart Money Moves
                                </h4>
                                <div className="space-y-2">
                                    {relevantWhales.map((trader, i) => (
                                        <div key={i} className="flex justify-between items-center bg-white p-3 rounded-lg border border-stone-100">
                                            <div className="flex items-center gap-2">
                                                <div className="w-6 h-6 rounded-full bg-stone-100 flex items-center justify-center text-xs font-bold">W</div>
                                                <div>
                                                    <div className="text-xs font-bold text-stone-900">{trader.address}</div>
                                                    <div className="text-[9px] text-stone-400">Win Rate: {trader.winRate}%</div>
                                                </div>
                                            </div>
                                            <div className={`text-xs font-bold ${trader.lastTrade.type === 'BUY' ? 'text-green-600' : 'text-red-600'}`}>
                                                {trader.lastTrade.type}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="sticky bottom-0 md:absolute md:bottom-0 left-0 w-full p-4 border-t border-stone-100 bg-white/95 backdrop-blur-sm z-20 flex flex-col gap-3 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] md:shadow-none">
                        
                        <div className="flex items-center justify-between px-3 py-2.5 bg-stone-50 rounded-xl border border-stone-100">
                            <span className="text-[10px] font-bold text-stone-400 uppercase">Contract</span>
                            <div className="flex items-center gap-2">
                                <span className="font-mono text-xs text-stone-600 truncate max-w-[120px] md:max-w-[200px]">
                                    {token.properties?.mintAddress || 'Pending...'}
                                </span>
                                <button 
                                    onClick={() => navigator.clipboard.writeText(token.properties?.mintAddress || '')}
                                    className="text-stone-400 hover:text-stone-900 transition-colors"
                                    title="Copy"
                                >
                                    <Copy size={14} />
                                </button>
                                {token.properties?.mintAddress && (
                                    <a 
                                        href={isSui ? `https://suiscan.xyz/mainnet/object/${token.properties.mintAddress}` : `https://solscan.io/token/${token.properties.mintAddress}`} 
                                        target="_blank" rel="noreferrer" 
                                        className="text-stone-400 hover:text-stone-900 transition-colors"
                                        title="View Explorer"
                                    >
                                        <ExternalLink size={14} />
                                    </a>
                                )}
                            </div>
                        </div>

                        <button 
                            onClick={() => { onClose(); onTrade(); }}
                            className="w-full py-3.5 bg-stone-900 text-white rounded-xl font-bold text-sm hover:bg-nobel-gold hover:text-stone-900 transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2 group transform active:scale-[0.99]"
                        >
                            Trade {token.symbol} <ArrowLeftRight size={16} className="group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>
                </div>

            </div>
        </div>
    );
};